import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v7";
import { RouterProvider } from "react-router/dom";

import { RootErrorBoundary } from "~/components/root-error-boundary";
import { LoadingOverlay } from "./components/loading-overlay";
import { authMiddleware } from "./lib/auth-middleware";
import { tenantContextMiddleware } from "./lib/tenant-context-middleware";
import { tenantDashboardRedirectMiddleware } from "./lib/tenant-dashboard-redirect-middleware";
import { tenantMiddleware } from "./lib/tenant-middleware";

const router = createBrowserRouter([
  {
    id: "root",
    path: "/",
    lazy: {
      Component: async () => (await import("./app/app")).App,
    },
    middleware: [authMiddleware, tenantContextMiddleware],
    hydrateFallbackElement: (
      <LoadingOverlay loading usePortal message="Loading page..." />
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      {
        id: "index",
        index: true,
        lazy: {
          Component: async () => (await import("./routes/index")).Index,
        },
      },

      // Tenant routes
      {
        id: "/t",
        path: "t",
        children: [
          // Tenant selector page
          {
            path: "",
            middleware: [tenantDashboardRedirectMiddleware],
            lazy: {
              Component: async () =>
                (await import("./routes/dashboard/__layout")).DashboardLayout,
            },
            children: [
              {
                index: true,
                lazy: {
                  Component: async () =>
                    (await import("./routes/dashboard/index")).Dashboard,
                  loader: async () =>
                    (await import("./routes/dashboard/loader")).loader,
                },
              },
            ],
          },

          // Tenant-specific routes
          {
            id: "/t/:tenantId",
            path: ":tenantId",
            middleware: [tenantMiddleware],
            children: [
              // Sidebar (dashboard) layout routes
              {
                lazy: {
                  Component: async () =>
                    (await import("./routes/dashboard/__layout"))
                      .DashboardLayout,
                },
                children: [
                  // Dashboard index
                  {
                    index: true,
                    lazy: {
                      Component: async () =>
                        (await import("./routes/dashboard/index")).Dashboard,
                      loader: async () =>
                        (await import("./routes/dashboard/loader")).loader,
                    },
                  },

                  // Processes list
                  {
                    id: "/t/:tenantId/processes",
                    path: "processes",
                    lazy: {
                      Component: async () =>
                        (await import("./routes/dashboard/processes/index"))
                          .ProcessesPage,
                      loader: async () =>
                        (await import("./routes/dashboard/processes/loader"))
                          .loader,
                    },
                  },

                  // Process runs
                  {
                    id: "/t/:tenantId/processes/:processId/runs",
                    path: "processes/:processId/runs",
                    lazy: {
                      Component: async () =>
                        (
                          await import(
                            "./routes/dashboard/processes/$processId/runs/index"
                          )
                        ).ProcessRunsPage,
                      loader: async () =>
                        (
                          await import(
                            "./routes/dashboard/processes/$processId/runs/loader"
                          )
                        ).loader,
                    },
                    // Prevent loader revalidation when only search params change (e.g., unitRecordId)
                    // Only revalidate if the actual path (processId) changes
                    shouldRevalidate: ({ currentUrl, nextUrl }) =>
                      currentUrl.pathname !== nextUrl.pathname,
                  },

                  // Settings
                  {
                    id: "/t/:tenantId/settings",
                    path: "settings",
                    lazy: {
                      Component: async () =>
                        (await import("./routes/settings/index")).SettingsPage,
                    },
                  },

                  // Process flow visualization
                  {
                    id: "/t/:tenantId/processes/:processId/runs/:runId/flow",
                    path: "processes/:processId/runs/:runId/flow",
                    lazy: {
                      Component: async () =>
                        (
                          await import(
                            "./routes/dashboard/processes/$processId/flow/$runId/index"
                          )
                        ).ProcessFlowPage,
                      loader: async () =>
                        (
                          await import(
                            "./routes/dashboard/processes/$processId/flow/$runId/loader"
                          )
                        ).loader,
                    },
                  },
                ],
              },

              // No sidebar routes

              // Process authoring canvas
              {
                id: "/t/:tenantId/processes/:processId/canvas",
                path: "processes/:processId/canvas",
                lazy: {
                  loader: async () =>
                    (await import("./routes/process/loader")).loader,
                  Component: async () =>
                    (await import("./routes/process/$id")).ProcessPage,
                },
              },

              // Step docs
              {
                id: "/t/:tenantId/steps/:stepId/docs",
                path: "steps/:stepId/docs",
                lazy: {
                  Component: async () =>
                    (await import("./routes/steps/$stepId/docs")).StepDocsPage,
                },
              },

              // Catch-all for tenant routes
              {
                path: "*",
                lazy: {
                  Component: async () =>
                    (await import("./routes/not-found")).NotFoundPage,
                },
              },
            ],
          },
        ],
      },

      // Non-tenant routes

      {
        id: "/auth/callback",
        path: "auth/callback",
        Component: () => (
          <LoadingOverlay loading usePortal message="Loading page..." />
        ),
      },

      // Not found page
      {
        id: "/not-found",
        path: "not-found",
        lazy: {
          Component: async () =>
            (await import("./routes/not-found")).NotFoundPage,
        },
      },

      // Global catch-all: Not found page
      {
        path: "*",
        lazy: {
          Component: async () =>
            (await import("./routes/not-found")).NotFoundPage,
        },
      },
    ],
  },
]);

export const Router = () => {
  return <RouterProvider router={router} />;
};
